import { memo } from 'react';
import classNames from 'classnames';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import LogLine from 'components/search/LogLine';
import { areEqual, checkIsActive, SearchLogLineProps } from 'components/search/SearchLogsPage/LogResultsContainer.hooks';

/**
 * This component represents a single log line when the user has chosen to view search results as wrapped log lines with
 * no horizontal scrolling.
 *
 * @prop edge            {@link LogEntryEdge} for this index in the list
 * @prop filter          the filter text to be highlighted
 * @prop index           index of this line in the data
 * @prop settings        user settings (some control which fields are displayed)
 * @prop width           width from the parent cell
 */
function WrappedLogLine(props: SearchLogLineProps): JSX.Element {
  const {
    data: {
      filter,
      logEntryEdges,
      settings,
      timezone,
    },
    index,
    style,
  } = props;

  const continuationToken = logEntryEdges[index]?.continuationToken || '';
  const logEntry = logEntryEdges[index]?.logEntry;
  const isActive = checkIsActive(props);

  const logLineClass = classNames(
    'LogLine',
    'LogLine--Wrapped',
    {
      'WrappedLogResults__Row--Active': isActive,
      'LogLine--RegularFont': settings.fontFamily === K.FONT_FAMILY.REGULAR,
    },
  );

  return (
    <div
      style={style}
      className={logLineClass}
      data-continuation-token={continuationToken}
      data-test-id={ELEMENT.searchLogsLogLine}
    >
      <LogLine
        index={index}
        continuationToken={continuationToken}
        logEntry={logEntry}
        filter={filter}
        settings={settings}
        timezone={timezone}
        disableLineBreaks={false}
      />
    </div>
  );
}

export default memo(WrappedLogLine, areEqual);
